import React, {useState, useEffect, useCallback} from 'react';
import './AssetSelector.scss';
import assetModel from '../../model/asset';
import ImageAsset from './ImageAsset';
import FileAsset from './FileAsset';
import {func, number, bool} from 'prop-types';
import {useTags} from '../../providers/TagsProvider';
import {Button, ControlLabel, FormGroup} from 'react-bootstrap';
import TagSuggestions from '../misc/TagSuggestions';

const propTypes = {
  onSelect: func.isRequired,
  pageSize: number,
  disabled: bool,
  isVideo: bool
};

const defaultProps = {
  pageSize: 12,
  disabled: false,
  isVideo: false
};

const AssetSelector = ({onSelect, pageSize, disabled, isVideo}) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const {getTags, lastFetched} = useTags();

  let mimetypes = 'image/*';

  if (isVideo) {
    mimetypes = 'video/*';
  }

  useEffect(() => {
    // Create an anonymous function and immediately call it to make use of await.
    // This is done because useEffect cant be async yet, so this is a workaround.
    (async () => {
      const tags = await getTags();
      setAllTags(tags);
    })();
  }, [lastFetched]);

  const handleLoadMore = async () => {
    const previous = result.items;
    const offset = previous.length;

    try {
      setSearching(true);

      const results = await assetModel.search({
        query: query,
        offset: offset,
        limit: pageSize,
        mimetype: mimetypes,
        tags: selectedTags.map((tag) => tag.id)
      });

      setResult({
        query: query,
        total: results.total,
        offset: results.offset,
        items: previous.concat(results.items),
        tags: selectedTags
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setSearching(true);

      const results = await assetModel.search({
        query: query,
        limit: pageSize,
        mimetype: mimetypes,
        tags: selectedTags.map((tag) => tag.id)
      });

      setResult({
        query: query,
        limit: pageSize,
        total: results.total,
        offset: results.offset,
        items: results.items,
        tags: selectedTags
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  const onDelete = useCallback((tagIndex) => {
    const updatedTags = selectedTags.filter((_, i) => i !== tagIndex);

    setSelectedTags(updatedTags);
  }, [selectedTags]);

  const onAddition = useCallback((newTag) => {
    if (selectedTags.some((tag) => tag.id === newTag.id)) {
      return;
    }

    const updatedTags = Array.from(selectedTags);
    updatedTags.push(newTag);

    setSelectedTags(updatedTags);
  }, [selectedTags]);

  return (
    <section className="contentful-asset-selector">
      <form onSubmit={handleSearch}>
        <div className="form-group has-feedback">
          <input
            type="text"
            placeholder="Søk..."
            className="form-control"
            value={query}
            onChange={handleQueryChange}
            disabled={searching}
            autoFocus={true}
          />
          <span className="form-control-feedback" aria-hidden="true">
            {searching &&
              <i className="fa fa-spinner fa-pulse fa-fw"></i>
            }
            {!searching &&
              <i className="fa fa-search"></i>
            }
          </span>
        </div>
        <FormGroup>
          <ControlLabel>Tags <small>Ved søk på flere tagger vises kun resultater som har alle taggene</small></ControlLabel>
          <TagSuggestions
            tags={selectedTags}
            suggestions={allTags}
            minQueryLength={0}
            onDelete={onDelete}
            onAddition={onAddition}
            allowNew={false}
            maxSuggestionsLength={30}
            placeholderText='Søk etter filer via tags'
            className="form-control"
            disabled={searching}
          />
        </FormGroup>
        <Button bsStyle="primary" type='submit'>Søk</Button>
      </form>
      <div style={{zIndex: 1}}>
        {Boolean(result) && !result.items.length &&
          <h2 className="h3">Ingen treff på &laquo;{result.query}&raquo; {result.tags.length > 0 ? `med tags: ${result.tags.map((tag) => tag.name).join(', ')}` : ''}</h2>
        }
        {Boolean(result) && Boolean(result.items.length) &&
          <h2 className="h3">{result.total} treff på &laquo;{result.query}&raquo; {result.tags.length > 0 ? `med tags: ${result.tags.map((tag) => tag.name).join(', ')}` : ''}</h2>
        }
        <ul className="contentful-asset-list list-unstyled">
          {Boolean(result) && result.items.map((asset) => {
            return (
              <li key={asset.id} className="contentful-asset-list-item">
                {!isVideo ? <ImageAsset
                  asset={asset}
                  disabled={disabled}
                  onSelect={onSelect}
                  aspect={1}
                  compact={true}
                /> : <FileAsset
                  asset={asset}
                  disabled={disabled}
                  onSelect={onSelect}
                />}
              </li>
            );
          })}
        </ul>
        {Boolean(result) && result.total > result.items.length &&
          <p className="text-center">
            <a
              className={`${searching ? 'disabled ' : ''}btn btn-default`}
              onClick={handleLoadMore}
            >
              {searching &&
                <span><i className="fa fa-spinner fa-pulse fa-fw"></i> Laster...</span>
              }
              {!searching &&
                <span>Last flere treff <span className="badge">{result.total - result.items.length}</span></span>
              }
            </a>
          </p>
        }
      </div>
    </section>
  );
};

AssetSelector.propTypes = propTypes;
AssetSelector.defaultProps = defaultProps;

export default AssetSelector;
